import React, { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { DatePicker } from "../datePicker";
import SearchDropdown from '../Settings/TaskManagementSettings/SearchDropdown';
import { displayToastMsg } from '../toastMessages/toastmessages';

interface AddInfileTaskModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
    allLawyers: any[];
    myTasks: any[];
    tasks: any[];
    SESSION_INFO: any;
    onAddTask: (task: any) => void;
}

// Extract meaningful content from task title using nested parenthesis logic (same as KeyDates.tsx)
const parseTaskTitle = (fullTitle: string): string => {
    // Check for nested parentheses pattern
    const nestedParenthesesRegex = /\(([^()]*\([^()]*\)[^()]*)\)/;
    const nestedMatches = fullTitle.match(nestedParenthesesRegex);
    
    if (nestedMatches) {
        // We found nested parentheses, return the outer group content
        return nestedMatches[1];
    } else {
        // No nested parentheses, try to get second parenthesis group
        const secondBracketRegex = /.*\(([^)]*)\)(?!.*\([^)]*\))/;
        const matches = fullTitle.match(secondBracketRegex);
        return matches ? matches[1] : fullTitle;
    }
};

const AddInfileTaskModal: React.FC<AddInfileTaskModalProps> = ({
    show,
    setShow,
    allLawyers,
    myTasks,
    tasks,
    SESSION_INFO,
    onAddTask
}) => {
    // State for the new task
    const [taskDescription, setTaskDescription] = useState<string>("");
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [taskDate, setTaskDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Reset form on modal close
    const handleClose = () => {
        setTaskDescription("");
        setSelectedUsers([]);
        setTaskDate(new Date().toISOString().split('T')[0]);
        setValidationError(null);
        setShow(false);
    };

    // Handle user selection/deselection
    const handleUserToggle = (userId: string) => {
        setSelectedUsers(prevUsers => {
            if (prevUsers.includes(userId)) {
                return prevUsers.filter(id => id !== userId);
            } else {
                return [...prevUsers, userId];
            }
        });
    };

    // Find largest typeID > 999999 in existing tasks
    const findNextTypeId = () => {
        const userTasks = tasks.filter(task => task.typeID > 999999);
        if (userTasks.length === 0) {
            return 1000000; // Start at 1000000 if no custom tasks exist
        }
        
        const maxTypeId = Math.max(...userTasks.map(task => task.typeID));
        return maxTypeId + 1;
    };

    // Create new task
    const handleSave = () => {
        // Validate inputs
        if (!taskDescription.trim()) {
            setValidationError("Task description is required");
            return;
        }
        
        if (selectedUsers.length === 0) {
            setValidationError("At least one user must be assigned");
            return;
        }
        
        // Check for duplicate task names
        const newTaskTitleParsed = parseTaskTitle(`(${taskDescription.trim()})`);
        const existingTaskTitles = myTasks
            .filter(task => task.isActivated === 1) // Only check active tasks
            .map(task => parseTaskTitle(task.title))
            .filter(title => title); // Remove empty titles
        
        const isDuplicate = existingTaskTitles.some(existingTitle => 
            existingTitle.toLowerCase() === newTaskTitleParsed.toLowerCase()
        );
        
        if (isDuplicate) {
            setValidationError("Cannot use duplicate task names");
            return;
        }
        
        // Get recordID (appId) and ownerID from existing tasks
        const sampleTask = myTasks[0];
        if (!sampleTask) {
            setValidationError("Unable to create task: Missing file information");
            return;
        }
        
        const recordId = sampleTask.appId;
        const ownerId = SESSION_INFO.firm.id.toString();
        
        // Find next available typeID
        const nextTypeId = findNextTypeId();
        
        // Create new task using addNewTask function
        const newTask = {
            typeID: nextTypeId.toString(),
            appId: recordId,
            ownerID: ownerId,
            date: taskDate,
            title: `(${taskDescription})`,
            isActivated: 1,
            isOverwriteActivate: 0,
            userResponsible: selectedUsers.join(','),
            overwriteDate: 1,
            isCompleted: 0,
            notes: null,
            overwritePerson: 1
        };
        
        // Call the onAddTask callback from parent component
        onAddTask(newTask);
        
        // Show success toast
        displayToastMsg({
            id: Date.now(),
            type: "success",
            msg: "Task Created Successfully"
        }, null);
        
        // Close modal
        handleClose();
    };

    return (
        <Modal size="sm" className="modal-sm-long" centered show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <h2>Add Task</h2>
            </Modal.Header>
            <Modal.Body>
                {validationError && (
                    <Alert variant="danger" className="mb-3">
                        {validationError}
                    </Alert>
                )}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Task Description</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={taskDescription}
                            onChange={(e) => setTaskDescription(e.target.value)}
                            placeholder="Enter task description"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Assigned Users</Form.Label>
                        <SearchDropdown
                            allValues={
                                allLawyers.filter(user => !user.LSUCID).map(user => ({
                                    Name: user.Name,
                                    Id: user.SubUserId?.toString() || user.UserID?.toString()
                                }))
                            }
                            selectedValues={selectedUsers}
                            onValueChange={handleUserToggle}
                        />
                        {selectedUsers.length === 0 && (
                            <div className="text-danger small mt-1">At least one user must be assigned</div>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Task Date</Form.Label>
                        <DatePicker
                            date={taskDate}
                            setDate={(newDate) => setTaskDate(newDate)}
                            placeholder="Select date"
                        />
                    </Form.Group>
                </Form>
                
                {/* Info message about task scope */}
                <div className="d-flex align-items-center mt-3 p-2 bg-warning-subtle rounded">
                    <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                    <span className="small">This task will only appear on this file.</span>
                </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <Button variant="outline-secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddInfileTaskModal; 